import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import type { ClientSummary } from '@/features/Clients/Data';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import type { Solicitacao, Movimentacao, StatusSolicitacao } from '../../../models';
import { pendenciaSolicitacao } from '../../SolicitacaoDetailsPage/pendenciaMovimentacao';
import { concluirSolicitacao } from '../../../services';
import { useSolicitacaoMutations, SI_SOLICITACOES_QUERY_KEYS } from '../../../hooks/data';
import CancelarSolicitacaoModal from '../../SolicitacaoDetailsPage/components/CancelarSolicitacaoModal';
import SolicitacoesKanbanColunasModal from './SolicitacoesKanbanColunasModal';
import SolicitacoesKanbanColuna from './SolicitacoesKanbanColuna';
import { useSolicitacoesKanbanLargura } from './useSolicitacoesKanbanLargura';
import type { AgrupamentoKanban, ColunaCustomizada } from './solicitacoesKanban.types';
import { AGRUPAMENTO_KANBAN_LABELS } from './solicitacoesKanban.types';
import {
  computarItensKanban,
  computarColunas,
  statusOcultoPeloFiltro,
  mensagemColunaVazia,
  lerAgrupamentoPersistido,
  salvarAgrupamentoPersistido,
  lerColunasCustomPersistidas,
  salvarColunasCustomPersistidas,
  LARGURA_COLUNA_PADRAO,
} from './solicitacoesKanban.utils';

interface SolicitacoesKanbanProps {
  itens: Solicitacao[];
  /** Filtro de status da tela (fora deste componente) — usado só pra diferenciar "coluna
   * vazia porque não tem mesmo" de "coluna vazia porque o filtro tá escondendo" (mesma
   * lista vazia = sem filtro, ver SolicitacoesListPage). */
  statusSelecionados: StatusSolicitacao[];
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>;
  clientes: ClientSummary[];
  mostrarPendencia: boolean;
  /** `destacarPendencia` pede pra tela de detalhe já abrir com o banner "Próximo passo pra
   * concluir" piscando — usado quando o próprio Kanban redireciona pra lá em vez de deixar
   * um drag simplesmente falhar (ver `handleDrop`). */
  onSelect: (id: string, opcoes?: { destacarPendencia?: boolean }) => void;
}

/**
 * EXPERIMENTO (branch `experimento/layout-solicitacoes`): board Kanban com 3 modos de
 * agrupamento (persistido por usuário): Por Status (padrão, colunas fixas
 * Novo/Em Andamento/Concluída), Por Pendência (uma coluna por tipo de pendência, só entre
 * as Solicitações Em Andamento — mesmo critério de pendência do resto da tela) e
 * Personalizado (o usuário escolhe livremente colunas de status/alerta/pendência,
 * misturadas — configurado em `SolicitacoesKanbanColunasModal`; uma Solicitação pode
 * aparecer em mais de uma coluna nesse modo).
 *
 * Arrastar card só funciona coluna a coluna, dependendo se ela representa um status real
 * (`coluna.statusAlvo`, ver solicitacoesKanban.utils) — sempre no modo Por Status; nunca no
 * Por Pendência (nenhuma coluna lá é status); no Personalizado, só nas colunas que o
 * usuário configurou como status (pode misturar com colunas de alerta/pendência no mesmo
 * board — essas ficam sem `statusAlvo` e não recebem drag). Quando bate com uma ação que já
 * existe na tela de detalhe, arrastar exige a mesma coisa que ela (ver `handleDrop`).
 */
export default function SolicitacoesKanban({
  itens,
  statusSelecionados,
  movimentacoesPorSolicitacao,
  clientes,
  mostrarPendencia,
  onSelect,
}: SolicitacoesKanbanProps) {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { cancelarSolicitacao, reativarSolicitacao, atualizarOperacional } = useSolicitacaoMutations();

  const [agrupamento, setAgrupamento] = useState<AgrupamentoKanban>(() => lerAgrupamentoPersistido(user?.uid));
  const [colunasCustom, setColunasCustom] = useState<ColunaCustomizada[]>(() => lerColunasCustomPersistidas(user?.uid));
  const { largurasColuna, iniciarArrasteLargura } = useSolicitacoesKanbanLargura(user?.uid);
  const [modalColunasAberto, setModalColunasAberto] = useState(false);
  const [colunaSobreArraste, setColunaSobreArraste] = useState<string | null>(null);
  const [colunaDropRejeitado, setColunaDropRejeitado] = useState<string | null>(null);
  const [cancelamentoPendente, setCancelamentoPendente] = useState<Solicitacao | null>(null);

  const handleMudarAgrupamento = (novo: AgrupamentoKanban) => {
    setAgrupamento(novo);
    salvarAgrupamentoPersistido(user?.uid, novo);
  };

  const handleSalvarColunasCustom = (novas: ColunaCustomizada[]) => {
    setColunasCustom(novas);
    salvarColunasCustomPersistidas(user?.uid, novas);
  };

  const itensComputados = useMemo(
    () => computarItensKanban(itens, movimentacoesPorSolicitacao, clientes, mostrarPendencia),
    [itens, movimentacoesPorSolicitacao, clientes, mostrarPendencia]
  );

  const opcoesAlerta = useMemo(
    () => Array.from(new Set(itensComputados.map((i) => i.alerta?.texto).filter((v): v is string => Boolean(v)))).sort(),
    [itensComputados]
  );
  const opcoesPendencia = useMemo(
    () =>
      Array.from(new Set(itensComputados.map((i) => i.pendencia?.label).filter((v): v is string => Boolean(v)))).sort(),
    [itensComputados]
  );

  const { colunas, membros } = useMemo(
    () => computarColunas(agrupamento, itens, itensComputados, mostrarPendencia, colunasCustom),
    [agrupamento, itens, itensComputados, mostrarPendencia, colunasCustom]
  );

  /** Destaque vermelho rápido na coluna-alvo quando o drop é rejeitado sem redirecionar pra
   * lugar nenhum (nada acontece além do toast) — sem isso, um drop inválido é visualmente
   * indistinguível de "travou"/bugou, já que o card simplesmente volta pro lugar sem
   * feedback nenhum na própria coluna. */
  const rejeitarDrop = (chaveColuna: string, mensagem: string) => {
    toast.error(mensagem);
    setColunaDropRejeitado(chaveColuna);
    window.setTimeout(() => setColunaDropRejeitado((atual) => (atual === chaveColuna ? null : atual)), 1000);
  };

  /** `chaveColuna` é a chave da própria coluna que recebeu o drop (`coluna.chave`) — só no
   * modo Por Status ela é igual a `destino`; no Personalizado uma coluna de status tem
   * chave `status:${destino}`, então precisam ser dois parâmetros (senão o destaque de
   * rejeição pisca na coluna errada, ou em nenhuma). */
  const handleDrop = async (destino: StatusSolicitacao, id: string, chaveColuna: string) => {
    const solicitacao = itens.find((s) => s.id === id);
    if (!solicitacao) return;
    const origem = solicitacao.status;
    if (origem === destino) return;

    if (destino === 'concluida') {
      if (origem !== 'em_andamento') {
        rejeitarDrop(chaveColuna, 'Só é possível concluir uma solicitação "Em Andamento".');
        return;
      }
      // Confere localmente antes de tentar — se já sabemos o que falta (mesmo cálculo do
      // banner "Próximo passo pra concluir" na tela de detalhe), nem chama a Cloud
      // Function: abre a própria Solicitação direto, já mostrando o que resolver (banner
      // piscando, ver `destacarPendencia`), em vez de só devolver um erro genérico depois
      // de uma tentativa que já sabíamos que ia falhar.
      const movimentacoes = movimentacoesPorSolicitacao.get(solicitacao.id) ?? [];
      const pendenciaAtual = pendenciaSolicitacao(solicitacao, movimentacoes);
      if (pendenciaAtual) {
        toast.error(`Falta ${pendenciaAtual.emoji} ${pendenciaAtual.label} — abrindo a Solicitação pra resolver.`);
        onSelect(solicitacao.id, { destacarPendencia: true });
        return;
      }
      try {
        await concluirSolicitacao(solicitacao.id);
        toast.success('Solicitação concluída.');
        queryClient.invalidateQueries({ queryKey: SI_SOLICITACOES_QUERY_KEYS.all });
      } catch (error: any) {
        toast.error(`Não foi possível concluir: ${error?.message || 'requisitos pendentes'}`);
        onSelect(solicitacao.id, { destacarPendencia: true });
      }
      return;
    }

    if (destino === 'cancelada') {
      if (origem !== 'aberta' && origem !== 'em_andamento') {
        rejeitarDrop(chaveColuna, 'Essa solicitação não pode ser cancelada a partir do status atual.');
        return;
      }
      setCancelamentoPendente(solicitacao);
      return;
    }

    if (destino === 'em_andamento' && origem === 'cancelada') {
      reativarSolicitacao.mutate({ id: solicitacao.id });
      return;
    }

    rejeitarDrop(chaveColuna, 'Esse status não pode ser definido arrastando o card — abra a Solicitação pra essa ação.');
  };

  const handleConfirmarCancelamento = (motivo: string, solicitacaoRelacionadaId: string | null) => {
    if (!user || !cancelamentoPendente) return;
    const id = cancelamentoPendente.id;

    cancelarSolicitacao.mutate(
      {
        id,
        canceledBy: { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' },
        motivoCancelamento: motivo,
      },
      {
        onSuccess: () => {
          setCancelamentoPendente(null);
          if (solicitacaoRelacionadaId) {
            atualizarOperacional.mutate({ id, solicitacaoRelacionadaId });
          }
        },
      }
    );
  };

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 rounded-md border border-gray-200 p-0.5">
          {(Object.keys(AGRUPAMENTO_KANBAN_LABELS) as AgrupamentoKanban[]).map((opcao) => (
            <button
              key={opcao}
              type="button"
              onClick={() => handleMudarAgrupamento(opcao)}
              className={`rounded px-2.5 py-1 text-xs font-medium ${
                agrupamento === opcao ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {AGRUPAMENTO_KANBAN_LABELS[opcao]}
            </button>
          ))}
        </div>
        {agrupamento === 'customizado' && (
          <button
            type="button"
            onClick={() => setModalColunasAberto(true)}
            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            <Cog6ToothIcon className="h-4 w-4" />
            Configurar colunas
          </button>
        )}
      </div>

      {agrupamento === 'pendencia' && !mostrarPendencia && (
        <p className="mb-3 text-xs text-gray-400">Pendência só é calculada na aba "Controle de Solicitações".</p>
      )}
      {agrupamento === 'pendencia' && mostrarPendencia && statusOcultoPeloFiltro(statusSelecionados, 'em_andamento') && (
        <p className="mb-3 text-xs text-amber-600">
          O filtro de Status está escondendo "Em Andamento" — sem elas, nenhuma pendência aparece aqui. Ajuste em
          Filtros.
        </p>
      )}
      {agrupamento === 'customizado' && colunasCustom.length === 0 && (
        <p className="mb-3 text-xs text-gray-400">
          Nenhuma coluna configurada ainda — use "Configurar colunas" pra escolher o que ver aqui.
        </p>
      )}

      <div className="flex gap-4 overflow-x-auto pb-2">
        {colunas.map((coluna) => {
          const arrastavel = coluna.statusAlvo != null;
          const itensDaColuna = membros.get(coluna.chave) ?? [];
          const mensagemVazia = mensagemColunaVazia(coluna, itensDaColuna, statusSelecionados);
          return (
            <SolicitacoesKanbanColuna
              key={coluna.chave}
              coluna={coluna}
              itensDaColuna={itensDaColuna}
              mensagemVazia={mensagemVazia}
              larguraPx={largurasColuna[coluna.chave] ?? LARGURA_COLUNA_PADRAO}
              onIniciarArrasteLargura={(event) => iniciarArrasteLargura(coluna.chave, event)}
              arrastavel={arrastavel}
              // Só explica a ausência de drag no Personalizado — no Por Pendência já existe
              // o aviso geral acima, e no Por Status toda coluna é arrastável mesmo.
              avisoSemDrag={
                !arrastavel && agrupamento === 'customizado'
                  ? 'Arrastar só funciona em colunas de Status — esta é de ' +
                    (coluna.chave.startsWith('alerta:') ? 'Alerta' : 'Pendência') +
                    '.'
                  : undefined
              }
              sobreArraste={colunaSobreArraste === coluna.chave}
              dropRejeitado={colunaDropRejeitado === coluna.chave}
              onDragEnter={() => setColunaSobreArraste(coluna.chave)}
              onDragLeave={() => setColunaSobreArraste((atual) => (atual === coluna.chave ? null : atual))}
              onDrop={(id) => {
                setColunaSobreArraste(null);
                if (coluna.statusAlvo) handleDrop(coluna.statusAlvo, id, coluna.chave);
              }}
              onSelect={onSelect}
            />
          );
        })}
      </div>

      <CancelarSolicitacaoModal
        isOpen={cancelamentoPendente != null}
        onClose={() => setCancelamentoPendente(null)}
        idAtual={cancelamentoPendente?.id ?? ''}
        onConfirmar={handleConfirmarCancelamento}
        confirmando={cancelarSolicitacao.isPending}
      />

      <SolicitacoesKanbanColunasModal
        isOpen={modalColunasAberto}
        onClose={() => setModalColunasAberto(false)}
        colunasIniciais={colunasCustom}
        opcoesAlerta={opcoesAlerta}
        opcoesPendencia={opcoesPendencia}
        onSalvar={handleSalvarColunasCustom}
      />
    </div>
  );
}
